import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CandidateDashboardLayout from '../components/layouts/CandidateDashboardLayout';
import Icon from '../components/atoms/Icon';
import SessionManager from '../utils/sessionManager';
import { getCandidateProfile } from '../services/api';

const CandidateProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');

  const session = SessionManager.getSession('candidate');
  const candidateId = session?.user_id;
  const candidateEmail = session?.email;

  useEffect(() => {
    if (!candidateId) {
      navigate('/login');
      return;
    }
    loadProfile();
  }, [candidateId, navigate, location.state]);

  const loadProfile = async () => {
    try {
      setLoading(true);
      // Add timestamp to bust cache
      const data = await getCandidateProfile(candidateId, candidateEmail, { _t: Date.now() });
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profile', icon: 'person' },
    { id: 'work', label: 'Work information', icon: 'work' },
    { id: 'education', label: 'Education', icon: 'school' },
    { id: 'projects', label: 'Projects', icon: 'code' },
    { id: 'certifications', label: 'Certifications', icon: 'workspace_premium' },
  ];

  // Check if profile is complete
  const isProfileComplete = (() => {
    if (!profile) return false;
    const fullName = profile?.full_name || `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim();
    const hasBasic = !!(fullName && profile?.phone);
    const hasDetails = !!(
      (profile?.education && profile.education.length > 0) ||
      (profile?.work_experience && profile.work_experience.length > 0) ||
      (profile?.skills && profile.skills.length > 0)
    );
    return hasBasic && hasDetails;
  })();

  if (loading) {
    return (
      <CandidateDashboardLayout>
        <div className="flex items-center justify-center h-full">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  // Show "Complete Your Profile" screen when profile is incomplete
  if (!isProfileComplete) {
    return (
      <CandidateDashboardLayout>
        <div className="max-w-2xl mx-auto mt-12">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Top accent */}
            <div className="h-1.5 bg-gradient-to-r from-[#0a1929] via-blue-600 to-[#0a1929]" />

            <div className="p-10 text-center">
              {/* Icon */}
              <div className="inline-flex items-center justify-center w-20 h-20 bg-[#0a1929] rounded-2xl mb-6">
                <Icon name="person_add" size={36} className="text-white" />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Complete Your Profile
              </h2>
              <p className="text-gray-500 max-w-md mx-auto mb-8">
                Set up your profile to unlock job recommendations, AI interview training, and earn bonus credits.
              </p>

              {/* What's needed */}
              <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left max-w-sm mx-auto">
                <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">What's needed</p>
                <div className="space-y-2.5">
                  {[
                    { label: 'Full name', done: !!(profile?.full_name || profile?.first_name) },
                    { label: 'Phone number', done: !!profile?.phone },
                    { label: 'Skills or Experience', done: !!((profile?.skills?.length > 0) || (profile?.work_experience?.length > 0) || (profile?.education?.length > 0)) },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-2.5">
                      <Icon
                        name={item.done ? 'check_circle' : 'radio_button_unchecked'}
                        size={18}
                        className={item.done ? 'text-green-500' : 'text-gray-300'}
                      />
                      <span className={`text-sm ${item.done ? 'text-gray-600 line-through' : 'text-gray-900 font-medium'}`}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={() => navigate('/onboarding')}
                className="px-8 py-3 bg-[#0a1929] hover:bg-[#0d2137] text-white font-semibold rounded-xl transition-colors inline-flex items-center gap-2"
              >
                <Icon name="edit" size={18} />
                Complete Profile
              </button>

              <p className="text-xs text-gray-400 mt-4">
                It only takes a few minutes
              </p>
            </div>
          </div>
        </div>
      </CandidateDashboardLayout>
    );
  }

  return (
    <CandidateDashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Breadcrumb */}
        <div className="mb-4 text-sm text-gray-600">
          <span>Candidate / Profile / </span>
          <span className="text-teal-600 font-medium">Profile detail</span>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-t-xl border-b border-gray-200 mb-0">
          <div className="flex gap-1 px-6">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-4 text-sm font-medium transition-colors relative ${
                  activeTab === tab.id
                    ? 'text-teal-600 border-b-2 border-teal-600'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content Card */}
        <div className="bg-white rounded-b-xl shadow-sm border border-gray-200 border-t-0 p-8">
          {/* Profile Header */}
          <div className="flex items-start justify-between mb-8 pb-6 border-b border-gray-200">
            <div className="flex items-center gap-6">
              <div className="w-24 h-24 rounded-full bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center text-white text-4xl font-bold">
                {profile?.first_name?.charAt(0) || 'U'}
              </div>
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {profile?.first_name} {profile?.last_name}
                </h2>
                <p className="text-teal-600 font-medium mb-1">{profile?.job_role || 'Not specified'}</p>
                <p className="text-gray-600">{profile?.company || 'Department'}</p>
              </div>
            </div>
            <div className="text-right">
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-1">Candidate ID:</p>
                <p className="font-semibold text-gray-900">{candidateId || '—'}</p>
              </div>
              <div className="mb-3">
                <p className="text-sm text-gray-600 mb-1">Phone number:</p>
                <p className="font-semibold text-gray-900">{profile?.phone_number || profile?.phone || '—'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Email:</p>
                <p className="font-semibold text-gray-900">{profile?.email || '—'}</p>
              </div>
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'profile' && (
            <div className="space-y-8">
              {/* Personal Information */}
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-lg font-semibold text-gray-900">Personal information</h3>
                  <button
                    onClick={() => navigate('/candidate/profile/edit')}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <Icon name="edit" size={20} />
                  </button>
                </div>
                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Gender</p>
                    <p className="font-medium text-gray-900">{profile?.gender || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Date of birth</p>
                    <p className="font-medium text-gray-900">{profile?.date_of_birth || profile?.dob || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">National ID</p>
                    <p className="font-medium text-gray-900">{profile?.national_id || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Nationality</p>
                    <p className="font-medium text-gray-900">{profile?.nationality || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">City/Location</p>
                    <p className="font-medium text-gray-900">{profile?.city || profile?.location || '—'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Marital status</p>
                    <p className="font-medium text-gray-900">{profile?.marital_status || 'Single'}</p>
                  </div>
                </div>
              </div>

              {/* Skills */}
              {profile?.skills && profile.skills.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Skills</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.skills.map((skill, index) => (
                      <span
                        key={index}
                        className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Resume */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Resume</h3>
                {profile?.resume_url || profile?.resume_filename ? (
                  <div className="flex items-center gap-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="description" size={22} className="text-green-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{profile.resume_filename || 'Resume.pdf'}</p>
                      <p className="text-xs text-green-600">Uploaded</p>
                    </div>
                    {profile.resume_url && (
                      <button
                        onClick={() => {
                          if (profile.resume_url.startsWith('data:')) {
                            const newWindow = window.open('', '_blank');
                            if (newWindow) {
                              newWindow.document.write(`<html><head><title>${profile.resume_filename || 'Resume'}</title><style>body{margin:0}iframe{width:100%;height:100vh;border:none}</style></head><body><iframe src="${profile.resume_url}"></iframe></body></html>`);
                              newWindow.document.close();
                            }
                          } else {
                            window.open(profile.resume_url, '_blank');
                          }
                        }}
                        className="px-3 py-1.5 text-sm text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50 flex items-center gap-1"
                      >
                        <Icon name="open_in_new" size={16} />
                        View
                      </button>
                    )}
                    <button
                      onClick={() => navigate('/candidate/profile/edit')}
                      className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 flex items-center gap-1"
                    >
                      <Icon name="edit" size={16} />
                      Replace
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4 p-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg">
                    <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="upload_file" size={22} className="text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm text-gray-500">No resume uploaded yet</p>
                    </div>
                    <button
                      onClick={() => navigate('/candidate/profile/edit')}
                      className="px-4 py-2 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2"
                    >
                      <Icon name="cloud_upload" size={16} />
                      Upload Resume
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'work' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Work Experience</h3>
                <button 
                  onClick={() => navigate('/candidate/profile/edit')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="edit" size={20} />
                </button>
              </div>
              {profile?.work_experience && profile.work_experience.length > 0 ? (
                <div className="space-y-6">
                  {profile.work_experience.map((exp, index) => (
                    <div key={index} className="border-l-4 border-teal-600 pl-6 pb-6 border-b border-gray-200 last:border-0">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 text-lg mb-1">
                            {exp.position || exp.title || 'Position'}
                          </h4>
                          <p className="text-teal-600 font-medium mb-2">{exp.company || 'Company'}</p>
                        </div>
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                          {exp.duration || `${exp.start_date || 'Start'} - ${exp.end_date || 'Present'}`}
                        </span>
                      </div>
                      {exp.description && (
                        <p className="text-gray-700 text-sm leading-relaxed">{exp.description}</p>
                      )}
                      {exp.responsibilities && Array.isArray(exp.responsibilities) && exp.responsibilities.length > 0 && (
                        <ul className="mt-3 space-y-1">
                          {exp.responsibilities.map((resp, idx) => (
                            <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                              <span className="text-teal-600 mt-1">•</span>
                              <span>{resp}</span>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="work_outline" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No work experience added yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'education' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Education information</h3>
                <button 
                  onClick={() => navigate('/candidate/profile/edit')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="edit" size={20} />
                </button>
              </div>
              {profile?.education && profile.education.length > 0 ? (
                <div className="space-y-6">
                  {profile.education.map((edu, index) => {
                    const institution = edu.institution === 'Other' ? edu.institutionOther : edu.institution;
                    const degree = edu.degree === 'Other' ? edu.degreeOther : edu.degree;
                    const field = edu.field === 'Other' ? edu.fieldOther : edu.field;
                    
                    return (
                      <div key={index} className="flex items-start justify-between pb-6 border-b border-gray-200 last:border-0">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-1">
                            {degree || edu.qualification || 'Degree'}
                            {field && ` in ${field}`}
                          </h4>
                          <p className="text-teal-600 font-medium">{institution || edu.school || 'Institution'}</p>
                          {edu.description && (
                            <p className="text-sm text-gray-600 mt-2">{edu.description}</p>
                          )}
                        </div>
                        <span className="text-sm text-gray-600 whitespace-nowrap">
                          {edu.duration || `${edu.start_year || edu.startYear || 'Start'} - ${edu.end_year || edu.endYear || 'End'}`}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="school" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No education information added yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'projects' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Projects</h3>
                <button 
                  onClick={() => navigate('/candidate/profile/edit')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="edit" size={20} />
                </button>
              </div>
              {profile?.projects && profile.projects.length > 0 ? (
                <div className="space-y-6">
                  {profile.projects.map((project, index) => (
                    <div key={index} className="border-l-4 border-purple-500 pl-6 pb-6 border-b border-gray-200 last:border-0">
                      <h4 className="font-semibold text-gray-900 text-lg mb-2">{project.title || project.name || 'Project'}</h4>
                      {project.description && (
                        <p className="text-gray-700 text-sm mb-3 leading-relaxed">{project.description}</p>
                      )}
                      {project.technologies && (
                        <div className="flex flex-wrap gap-2 mb-3">
                          {(Array.isArray(project.technologies) ? project.technologies : project.technologies.split(',')).map((tech, idx) => (
                            <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs font-medium">
                              {tech.trim()}
                            </span>
                          ))}
                        </div>
                      )}
                      {project.link && (
                        <a 
                          href={project.link} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1"
                        >
                          <Icon name="link" size={16} />
                          View Project
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="code" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No projects added yet</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'certifications' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Certifications</h3>
                <button 
                  onClick={() => navigate('/candidate/profile/edit')}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <Icon name="edit" size={20} />
                </button>
              </div>
              {profile?.courses && profile.courses.length > 0 ? (
                <div className="space-y-4">
                  {profile.courses.map((cert, index) => (
                    <div key={index} className="flex items-start gap-4 p-4 border border-gray-200 rounded-lg hover:bg-gray-50">
                      <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Icon name="workspace_premium" size={24} className="text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 mb-1">
                          {cert.name || cert.title || cert.course || 'Certification'}
                        </h4>
                        <p className="text-sm text-gray-600 mb-1">
                          {cert.issuer || cert.organization || cert.provider || 'Issuing Organization'}
                        </p>
                        {cert.date && (
                          <p className="text-xs text-gray-500">Issued: {cert.date}</p>
                        )}
                        {cert.credential_id && (
                          <p className="text-xs text-gray-500 mt-1">Credential ID: {cert.credential_id}</p>
                        )}
                        {cert.link && (
                          <a 
                            href={cert.link} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-teal-600 hover:text-teal-700 flex items-center gap-1 mt-2"
                          >
                            <Icon name="open_in_new" size={14} />
                            View Certificate
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Icon name="workspace_premium" size={48} className="text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No certifications added yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </CandidateDashboardLayout>
  );
};

export default CandidateProfilePage;
